import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  inject,
  viewChild,
} from '@angular/core';

import { CookieConsentService } from '@core/cookies/cookie-consent.service';

const HEIGHT_VAR = '--handel-cookie-banner-height';

@Component({
  selector: 'app-cookie-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section #banner class="cookie-banner" role="region" aria-labelledby="cookie-banner-title">
      <div class="cookie-banner__body">
        <h2 id="cookie-banner-title" class="cookie-banner__title">Su privacidad</h2>
        <p class="cookie-banner__text">
          Usamos cookies para mejorar su experiencia y mostrarle beneficios relevantes. Puede
          aceptarlas, rechazarlas o elegir cuáles usar.
          <a class="cookie-banner__policy" href="/informacion/politicas-de-privacidad"
            >Política de cookies</a
          >
        </p>
      </div>
      <div class="cookie-banner__actions">
        <button
          type="button"
          class="cookie-banner__link cookie-banner__link--desktop"
          (click)="openSettings()"
        >
          Preferencias
        </button>
        <button
          type="button"
          class="cookie-banner__button cookie-banner__button--outline"
          (click)="reject()"
        >
          Rechazar
        </button>
        <button
          type="button"
          class="cookie-banner__button cookie-banner__button--primary"
          (click)="accept()"
        >
          Aceptar todo
        </button>
        <button
          type="button"
          class="cookie-banner__link cookie-banner__link--mobile"
          (click)="openSettings()"
        >
          Su privacidad
        </button>
      </div>
    </section>
  `,
  styles: `
    :host {
      display: contents;
    }

    .cookie-banner {
      position: fixed;
      left: 50%;
      bottom: 12px;
      z-index: 1000;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      width: min(364px, calc(100vw - 24px));
      padding: 25px 28px 24px;
      border-radius: 10px;
      background: #e6e6e7;
      box-shadow: 0 0 15px rgba(0, 0, 0, 0.75);
      color: #1e1e1e;
      font-family: 'Avenir Next', sans-serif;
      transform: translateX(-50%);
    }

    .cookie-banner__title {
      margin: 0;
      color: #1e1e1e;
      font-size: 20px;
      font-weight: 600;
      line-height: 27px;
    }

    .cookie-banner__text {
      margin: 8px 0 0;
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
    }

    .cookie-banner__policy {
      color: #1c66d6;
      text-decoration: underline;
    }

    .cookie-banner__actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      gap: 18px;
      margin-top: 16px;
    }

    .cookie-banner__button {
      box-sizing: border-box;
      flex: 1 1 0;
      max-width: 145px;
      min-width: 0;
      border-radius: 10px;
      font-family: inherit;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
    }

    .cookie-banner__button--outline {
      height: 44px;
      border: 1px solid #0b234f;
      background: #ffffff;
      color: #0b234f;
    }

    .cookie-banner__button--primary {
      height: 42px;
      border: 0;
      background: #0b234f;
      color: #ffffff;
    }

    .cookie-banner__link {
      padding: 0;
      border: 0;
      background: none;
      color: #0b234f;
      font-family: inherit;
      font-size: 16px;
      font-weight: 600;
      text-decoration: underline;
      cursor: pointer;
    }

    .cookie-banner__link--mobile {
      flex-basis: 100%;
      margin-top: 14px;
    }

    .cookie-banner__link--desktop {
      display: none;
    }

    .cookie-banner__button:focus-visible,
    .cookie-banner__link:focus-visible,
    .cookie-banner__policy:focus-visible {
      outline: 2px solid #0b234f;
      outline-offset: 2px;
    }

    @media (min-width: 768px) {
      .cookie-banner {
        left: 50%;
        bottom: 24px;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 32px;
        width: min(1140px, calc(100vw - 48px));
        padding: 20px 32px;
        background: #ffffff;
        box-shadow: 0 0 15px rgba(0, 0, 0, 0.25);
      }

      .cookie-banner__body {
        flex: 1;
        min-width: 0;
      }

      .cookie-banner__actions {
        flex-wrap: nowrap;
        flex-shrink: 0;
        justify-content: flex-end;
        gap: 16px;
        margin-top: 0;
      }

      .cookie-banner__button {
        flex: 0 0 145px;
        max-width: none;
      }

      .cookie-banner__link--mobile {
        display: none;
      }

      .cookie-banner__link--desktop {
        display: inline;
      }
    }
  `,
})
export class CookieBannerComponent implements AfterViewInit, OnDestroy {
  private readonly consent = inject(CookieConsentService);
  private readonly banner = viewChild.required<ElementRef<HTMLElement>>('banner');
  private observer: ResizeObserver | null = null;

  ngAfterViewInit(): void {
    const element = this.banner().nativeElement;
    const root = document.documentElement;
    const sync = () => root.style.setProperty(HEIGHT_VAR, `${element.offsetHeight}px`);
    this.observer = new ResizeObserver(sync);
    this.observer.observe(element);
    sync();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    document.documentElement.style.removeProperty(HEIGHT_VAR);
  }

  accept(): void {
    this.consent.acceptAll();
  }

  reject(): void {
    this.consent.rejectAll();
  }

  openSettings(): void {
    this.consent.openPreferences();
  }
}
